import { useRef, useMemo, useEffect } from 'react';

import { Box, Modal, ModalFooter, ModalHeader, BodySmall, AgGridTable, ColDef } from '@parspec/pixel';

import { useCompanyInfoQuery } from '../../../queries';
import { getHeadingForModal } from '../../../utils/utils';
import { useLocationDetails } from '../../../AddEditLocation/context';
import { getRowId } from './utils';
import { DEFAULT_COL_DEF, gridOptions } from './constants';

interface MultipleBranchesModalPropType {
    isOpen: boolean;
    onClose: () => void;
    currentTab?: string;
    onColumnSelection: (data: number[]) => void;
    isLoading: boolean
}


export const MultipleBranchesModal = ({ isOpen, onClose, currentTab, onColumnSelection, isLoading }: MultipleBranchesModalPropType) => {
    const { data: companyInfo, isLoading: isCompanyLoading } = useCompanyInfoQuery();
    const { currentLocationId } = useLocationDetails();

    const tableRef = useRef<any>();

    const closeModalHandler = () => {
        onClose();
    };

    const rowDataOptions = useMemo(() => {
        if (!companyInfo?.data) return [];

        const currentLocation = companyInfo.data.find(el => el.id === currentLocationId);
        const otherLocations = companyInfo.data.filter(el => el.id !== currentLocationId);

        return [
            ...(currentLocation ? [{ field: currentLocation.name, label: currentLocation.name, id: currentLocation.id }] : []),
            ...otherLocations.map(el => ({ field: el.name, label: el.name, id: el.id }))
        ];
    }, [companyInfo?.data, currentLocationId]);


    function onGridReady() {
        if (tableRef.current && tableRef.current.api) {
            tableRef.current.api.forEachNode((node: any) => {
                if (currentLocationId === node.data.id) {
                    node.setSelected(true);
                } else {
                    node.setSelected(false);
                }
            });
        }
    }

    useEffect(() => {
        if (rowDataOptions.length > 0 && !isCompanyLoading) {
            onGridReady();
        }
    }, [rowDataOptions, isCompanyLoading]);

    const onRowSelected = (event: any) => {
        if (event.node.data.id === currentLocationId && !event.node.selected) {
            event.node.setSelected(true, false);
        }
    };

    const onAccept = () => {
        const rowData: { field: string; label: string; id: number }[] = [];
        tableRef.current.api.forEachNode((node: any) => {
            if (node.selected) {
                rowData.push(node.data);
            }
        });
        const finalRowData = rowData.map((el: { field: string; label: string; id: number }) => el.id);
        onColumnSelection(finalRowData);
    };

    const columnDefs: ColDef[]  = useMemo(() => [
        {
            field: 'id',
            hide: true,
            lockPosition: 'left',
            resizable: false
        },
        {
            headerCheckboxSelection: true,
            checkboxSelection: (params: any) => {
                const isDisabled = params.data.id === currentLocationId;
                return !isDisabled;
            },
            width: 50,
            maxWidth: 50,
            resizable: false,
            showDisabledCheckboxes: true,
            headerCheckboxSelectionFilteredOnly: true
        },
        {
            field: 'label',
            minWidth: 100,
            headerName: 'Branch Location'
        }
    ], [currentLocationId]);
    


    const modalHeading = useMemo(() => {
        return `Save ${getHeadingForModal(currentTab ? currentTab : '')} Settings`
    }, [currentTab])

    return (
        <Box>
            <Modal
                open={isOpen}
                footer={<ModalFooter isLoading={isLoading} onAccept={onAccept} onReject={closeModalHandler} />}
                header={<ModalHeader onClose={closeModalHandler} title={modalHeading}></ModalHeader>}
            >
                <Box width={'400px'}>
                    <Box mb={2}>
                        <BodySmall color={'text.secondary'} limit={false}>
                            Select the branches you wish to copy these settings to. Existing data in these will be overwritten.
                        </BodySmall>
                    </Box>

                    <Box height={'400px'} width={'100%'}>
                        <AgGridTable
                            ref={tableRef}
                            getRowId={getRowId}
                            isLoading={isCompanyLoading}
                            rowHeight={40}
                            showToolbarPanel
                            rowData={isCompanyLoading || rowDataOptions?.length === 0 ? [] : rowDataOptions}
                            defaultColDef={DEFAULT_COL_DEF}
                            columnDefs={columnDefs}
                            suppressContextMenu
                            suppressMenuHide
                            gridOptions={gridOptions}
                            onGridReady={onGridReady}
                            onRowSelected={onRowSelected}
                        />
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};
